/**
 * Frontend configuration.
 *
 * No defaults. A missing API URL that silently falls back to localhost ships a
 * production build pointing at a machine that does not exist, and the failure
 * surfaces as an unexplained network error in a user's browser instead of at
 * build time where someone can fix it.
 *
 * NEXT_PUBLIC_* values are inlined at build, so this throws while building.
 */

/**
 * Where the browser sends API calls. A path, `/api`, not a hostname.
 *
 * Each app serves the API from its own host, so every call a page makes is
 * same-origin. That is not a detail of routing: it is what lets the session
 * cookie be host-only. While the API answered on a hostname of its own, the
 * cookie had to be scoped to the parent domain for three hosts to read it, both
 * products shared that one jar, and signing in to either ended the other's
 * session. Browsers that decline third-party cookies dropped it outright for
 * some visitors.
 *
 * Server components do not use this; a relative path means nothing to them. See
 * `session-server.ts`, which reaches the API inside the machine.
 */
export const API_URL = requirePublicEnv(
  'NEXT_PUBLIC_API_URL',
  process.env.NEXT_PUBLIC_API_URL,
);

function requirePublicEnv(name: string, value: string | undefined): string {
  if (!value?.trim()) {
    throw new Error(
      `${name} is not set. Add it to .env before building; it cannot be defaulted, ` +
        `or the build would silently point at the wrong API.`,
    );
  }
  return value.trim();
}
